import { useState } from 'react';
import { useMedplum } from '@medplum/react';
import { Encounter, Patient, CarePlan } from '@medplum/fhirtypes';
import { createReference } from '@medplum/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from '@mantine/hooks';

export const useSaveRecommendation = (
  patient?: Patient,
  encounter?: Encounter,
  recommendation?: string,
  initialRecommendation?: string,
  updateInitialRecommendation?: (value: string) => void
) => {
  const medplum = useMedplum();
  const { t } = useTranslation(['patient-pages']);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<Error | null>(null);

  const saveRecommendation = useDebouncedCallback(async () => {
    // Only save if there are changes
    if (recommendation === initialRecommendation) {
      return;
    }

    if (!encounter?.id || !patient?.id) {
      notifications.show({
        color: 'red',
        title: t('examination.notifications.noEncounterInProgress', {
          ns: 'patient-pages',
        }),
        message: t('examination.notifications.noEncounterMessage', {
          ns: 'patient-pages',
        }),
      });
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      const carePlan = await medplum.searchOne('CarePlan', {
        encounter: `Encounter/${encounter.id}`,
        subject: `Patient/${patient.id}`,
        status: 'active',
      });
      if (carePlan) {
        // Update existing CarePlan
        const updatedCarePlan: CarePlan = {
          ...carePlan,
          note: [
            {
              text: recommendation || '',
              time: new Date().toISOString(),
            },
          ],
        };

        await medplum.updateResource(updatedCarePlan);
      } else {
        // Create new CarePlan
        const newCarePlan: CarePlan = {
          resourceType: 'CarePlan',
          status: 'active',
          intent: 'plan',
          subject: createReference(patient),
          encounter: createReference(encounter),
          created: new Date().toISOString(),
          note: [
            {
              text: recommendation || '',
              time: new Date().toISOString(),
            },
          ],
        };

        await medplum.createResource(newCarePlan);
      }

      if (recommendation) {
        updateInitialRecommendation?.(recommendation);
      }

      // Update last saved timestamp on success
      setLastSaved(new Date());
      setSaveError(null);
    } catch (err) {
      console.error(err);
      setSaveError(err as Error);
    } finally {
      setSaving(false);
    }
  }, 1000);

  const retrySave = () => {
    saveRecommendation();
  };

  return {
    saveRecommendation,
    saving,
    lastSaved,
    saveError,
    retrySave,
  };
};
